import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileDown, FileSpreadsheet, AlertTriangle } from "lucide-react";
import Layout from "@/components/Layout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";

const RIGOR_LEVELS = ["maximum", "intermediate", "minimum"];
const RIGOR_LABELS = { maximum: "Máximo", intermediate: "Intermediário", minimum: "Mínimo" };

export default function Sensorial() {
  const [meta, setMeta] = useState(null);
  const [testType, setTestType] = useState("triangle");
  const [mode, setMode] = useState("difference");
  const [panel, setPanel] = useState("consumer");
  const [rigorIdx, setRigorIdx] = useState(1); // intermediate
  const [confidence, setConfidence] = useState("0.95");
  const [nCorrect, setNCorrect] = useState("");
  const [nTotal, setNTotal] = useState("");
  const [recommended, setRecommended] = useState(null);
  const [result, setResult] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [loading, setLoading] = useState(false);

  const rigor = RIGOR_LEVELS[rigorIdx];

  useEffect(() => {
    api.get("/sensory/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  // Auto-compute recommended sample size on parameter changes
  useEffect(() => {
    let cancelled = false;
    const params = { test_type: testType, mode, panel, rigor, confidence: parseFloat(confidence) };
    api.post("/sensory/sample-size", params).then((r) => {
      if (!cancelled) setRecommended(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [testType, mode, panel, rigor, confidence]);

  const submit = async (e) => {
    e.preventDefault();
    const nc = parseInt(nCorrect, 10);
    const nt = parseInt(nTotal, 10);
    if (isNaN(nc) || isNaN(nt) || nt <= 0 || nc < 0 || nc > nt) {
      return toast.error("Informe acertos e total válidos (0 ≤ acertos ≤ total)");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/sensory/analyze", {
        test_type: testType, mode, panel, rigor,
        confidence: parseFloat(confidence),
        n_correct: nc, n_total: nt,
      });
      setResult(data.result);
      setAnalysisId(data.id);
      toast.success("Análise concluída");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (kind) => {
    if (!analysisId) return;
    const token = localStorage.getItem("sensorize_token");
    const url = `${API_BASE}/sensory/${analysisId}/export/${kind}`;
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error("Falha ao exportar"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `sensorial_${analysisId}.${kind === "pdf" ? "pdf" : "xlsx"}`;
        a.click();
      })
      .catch((e) => toast.error(e.message));
  };

  const testsList = meta?.tests || [];

  const p = recommended?.parameters;

  return (
    <Layout>
      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#7CB342]">Testes de Escolha Forçada</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 mt-1">
          Testes de Discriminação
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Cálculo de tamanho amostral (Bi 2015, Meilgaard 5ª ed.) e análise de resultado com IC de Wilson.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-5 border-slate-200 h-fit">
          <CardContent className="p-6">
            <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">Configuração</h2>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label>Tipo de teste</Label>
                <Select value={testType} onValueChange={setTestType}>
                  <SelectTrigger data-testid="sensory-test-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {testsList.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.label} (p₀ = {t.p0.toFixed(3)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {recommended?.recommended_n != null && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="sensory-min-panelists">
                    Provadores mínimos sugeridos ({(parseFloat(confidence)*100).toFixed(0)}%): <strong>{recommended.recommended_n}</strong>
                  </p>
                )}
                {(() => {
                  const w = testsList.find((t) => t.id === testType)?.warning;
                  if (!w) return null;
                  return (
                    <div
                      className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3"
                      data-testid={`sensory-test-warning-${testType}`}
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-900 leading-relaxed">{w}</p>
                    </div>
                  );
                })()}
              </div>

              <div>
                <Label>Modo</Label>
                <RadioGroup value={mode} onValueChange={setMode} className="mt-1 grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer ${mode==="difference" ? "border-[#7CB342] bg-[#EEF7E4]" : "border-slate-200"}`}>
                    <RadioGroupItem value="difference" data-testid="mode-difference" />
                    <span className="text-sm font-medium">Diferença</span>
                  </label>
                  <label className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer ${mode==="similarity" ? "border-[#7CB342] bg-[#EEF7E4]" : "border-slate-200"}`}>
                    <RadioGroupItem value="similarity" data-testid="mode-similarity" />
                    <span className="text-sm font-medium">Similaridade</span>
                  </label>
                </RadioGroup>
              </div>

              <div>
                <Label>Painel</Label>
                <RadioGroup value={panel} onValueChange={setPanel} className="mt-1 grid grid-cols-3 gap-2">
                  <label className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer ${panel==="trained" ? "border-[#7CB342] bg-[#EEF7E4]" : "border-slate-200"}`}>
                    <RadioGroupItem value="trained" data-testid="panel-trained" />
                    <span className="text-sm font-medium">Treinado</span>
                  </label>
                  <label className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer ${panel==="selected" ? "border-[#7CB342] bg-[#EEF7E4]" : "border-slate-200"}`}>
                    <RadioGroupItem value="selected" data-testid="panel-selected" />
                    <span className="text-sm font-medium">Selecionado</span>
                  </label>
                  <label className={`flex items-center gap-2 border rounded-md p-2 cursor-pointer ${panel==="consumer" ? "border-[#7CB342] bg-[#EEF7E4]" : "border-slate-200"}`}>
                    <RadioGroupItem value="consumer" data-testid="panel-consumer" />
                    <span className="text-sm font-medium">Consumidor</span>
                  </label>
                </RadioGroup>
                {panel === "selected" && (
                  <p className="text-xs italic text-slate-500 mt-1">
                    Selecionado = ~30% mais provadores que treinado.
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-baseline justify-between">
                  <Label>Nível de rigor (P<sub>d</sub>)</Label>
                  {rigor === "intermediate" && (
                    <span className="text-xs italic text-[#7CB342]">Intermediário = equilíbrio recomendado</span>
                  )}
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2" data-testid="rigor-tabs">
                  {RIGOR_LEVELS.map((r, idx) => {
                    const selected = rigorIdx === idx;
                    const isRecommended = r === "intermediate";
                    const isWarning = r === "minimum";
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRigorIdx(idx)}
                        data-testid={`rigor-tab-${r}`}
                        className={`px-3 py-3 rounded-lg border-2 text-center transition-colors duration-200 ${
                          selected
                            ? isRecommended
                              ? "border-[#7CB342] bg-[#F2F9EC]"
                              : isWarning
                                ? "border-amber-500 bg-amber-50"
                                : "border-slate-800 bg-slate-50"
                            : "border-slate-200 bg-white hover:bg-slate-50"
                        }`}
                      >
                        <div className="text-sm font-heading font-semibold text-slate-900 flex items-center justify-center gap-1">
                          {r === "maximum" && "Máximo Rigor"}
                          {r === "intermediate" && "Intermediário"}
                          {r === "minimum" && (
                            <>
                              Mínimo <AlertTriangle className="w-4 h-4 text-amber-500" />
                            </>
                          )}
                        </div>
                        <div className="text-[11px] mt-0.5 font-semibold">
                          {isRecommended && <span className="text-[#7CB342]">Recomendado</span>}
                          {isWarning && <span className="text-amber-600">Aceitável</span>}
                          {r === "maximum" && <span className="text-slate-400">Rigoroso</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {p && (
                  <div
                    className="mt-3 rounded-lg border border-[#7CB342]/40 bg-[#F5FAEE] p-4 text-sm"
                    data-testid="rigor-info-panel"
                  >
                    <InfoRow label={<>Confiança (1−α):</>} value={`${((1 - p.alpha) * 100).toFixed(0)} %`} />
                    <InfoRow label={<>α (Erro do tipo I):</>} value={p.alpha.toFixed(2).replace(".", ",")} />
                    <InfoRow label={<>β (Erro do tipo II):</>} value={p.beta.toFixed(2).replace(".", ",")} />
                    <InfoRow label={<>p<sub>d</sub> (tolerância discriminadora):</>} value={`${(p.pd * 100).toFixed(0)} %`} />
                  </div>
                )}
              </div>

              <div>
                <Label>Nível de confiança</Label>
                <Select value={confidence} onValueChange={setConfidence}>
                  <SelectTrigger data-testid="sensory-confidence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.90">90% (α = 0,10)</SelectItem>
                    <SelectItem value="0.95">95% (α = 0,05)</SelectItem>
                    <SelectItem value="0.99">99% (α = 0,01)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Altera α, o IC de Wilson e o tamanho amostral recomendado.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Acertos</Label>
                  <Input
                    type="number" min="0"
                    value={nCorrect} onChange={(e) => setNCorrect(e.target.value)}
                    data-testid="sensory-correct-input"
                  />
                </div>
                <div>
                  <Label>Total de provadores</Label>
                  <Input
                    type="number" min="1"
                    value={nTotal} onChange={(e) => setNTotal(e.target.value)}
                    data-testid="sensory-total-input"
                  />
                </div>
              </div>

              <Button
                type="submit" disabled={loading}
                data-testid="sensory-analyze-button"
                className="w-full bg-[#7CB342] hover:bg-[#558B2F] text-white font-semibold"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Analisar
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="lg:col-span-7 space-y-6">
          {!result && (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="p-10 text-center text-slate-500">
                Configure os parâmetros e informe acertos/total para visualizar o resultado.
              </CardContent>
            </Card>
          )}

          {result && (
            <Card className="border-slate-200" data-testid="sensory-result-card">
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-xl font-heading font-bold text-slate-900">Resultado</h2>
                    <p className="text-sm text-slate-500">
                      {result.test_label} · {result.mode === "difference" ? "Diferença" : "Similaridade"} · {result.panel === "trained" ? "Treinado" : result.panel === "selected" ? "Selecionado" : "Consumidor"} · {RIGOR_LABELS[result.rigor]}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadFile("pdf")} data-testid="sensory-export-pdf">
                      <FileDown className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadFile("excel")} data-testid="sensory-export-excel">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Stat label="p estimada" value={result.results.p_hat.toFixed(4)} />
                  <Stat label="pd estimado" value={result.results.pd_hat.toFixed(4)} />
                  <Stat label="p-valor" value={result.results.p_value.toExponential(3)} />
                  <Stat label={`IC ${(result.confidence*100).toFixed(0)}% (Wilson)`} value={`[${result.results.wilson_ci_95.low.toFixed(3)}, ${result.results.wilson_ci_95.high.toFixed(3)}]`} />
                </div>

                <div className="mt-5 p-4 rounded-lg border border-slate-200 bg-slate-50">
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">Decisão</p>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge
                      className={result.results.significant ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}
                      data-testid="sensory-decision-badge"
                    >
                      {result.results.decision}
                    </Badge>
                    <span className="text-sm text-slate-500" data-testid="sensory-decision-summary">
                      {result.input.n_correct}/{result.input.n_total} respostas corretas
                      {result.results.threshold?.value != null && (
                        <>
                          {" · "}
                          <span className="text-slate-700">
                            {result.results.threshold.label}: <strong>{result.results.threshold.value}</strong>
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  {result.results.x_min_difference != null && (
                    <p className="text-xs text-slate-600 mt-3" data-testid="sensory-xmin-difference">
                      X<sub>mín</sub> para diferença: <strong>{result.results.x_min_difference}</strong> de {result.input.n_total} acertos
                      {" "}
                      <span className="text-slate-400">
                        (mínimo necessário para declarar diferença significativa com α = {result.parameters.alpha.toFixed(2).replace(".", ",")})
                      </span>
                    </p>
                  )}
                  {result.warning && (
                    <div
                      className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3"
                      data-testid="sensory-result-warning"
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-900 leading-relaxed">{result.warning}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-slate-700">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 p-3 bg-white">
      <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-heading font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
